import { AxiosRequestConfig } from 'axios'
import {
  BaseApiParams as BP,
  BaseApiResponse,
} from '@/typings/common'
import {
  Formdata as Data,
  FormdataCollection as Collection,
} from '@/typings/api/Formdata'

import { BaseLkApiAuth } from '@/api/BaseApiAuth'
import { DataCollection } from '@/typings/api/DataCollection'


export class LkFormdataApi extends BaseLkApiAuth<Data, Collection> {
  protected readonly endpoint = 'formdata';

  public getOne<P extends BP, R extends BaseApiResponse<Collection> = BaseApiResponse<Collection>>(
    id: string|number, rawParams?: P, config?: AxiosRequestConfig,
  ) {
    return super.getOne<P, R>(id, rawParams, config)
      .then((response) => ({ data: response }) as unknown as typeof response)
  }

  public opened(id: number, params?: Record<string, unknown>, config: AxiosRequestConfig = {}) {
    const path = `${id}/opened/`
    const [url] = this.getUrl(path)
    return this.request<never, DataCollection>('POST', url, { params, ...config })
  }

  public submit(id: number, params: Record<string, unknown>, config: AxiosRequestConfig = {}) {
    const path = `${id}/submit/`
    const [url] = this.getUrl(path)
    return this.request<never, DataCollection>('POST', url, { params, ...config })
  }

  public validate(id: number, params?: Record<string, unknown>, config: AxiosRequestConfig = {}) {
    const path = `${id}/validate/`
    const [url] = this.getUrl(path)
    return this.request<never, { data: Record<string, unknown>[] }>('GET', url, { params, ...config })
  }

  public history(id: number, params: Record<string, unknown> = {}, config: AxiosRequestConfig = {}) {
    const path = `${id}/history`
    const [url] = this.getUrl(path)

    return this.request<never, { data: Record<string, unknown>[] }>('GET', url, { params, ...config })
  }

  public revisions(id: number, params: Record<string, unknown> = {}, config: AxiosRequestConfig = {}) {
    const path = `${id}/revisions`
    const [url] = this.getUrl(path)

    return this.request<never, { data: Record<string, unknown>[] }>('GET', url, { params, ...config })
  }

  public revision(id: number, params: Record<string, unknown> = {}, config: AxiosRequestConfig = {}) {
    const path = `${id}/revisions/:revision_id`
    const [url, paramsNew] = this.getUrl(path, params)

    return this.request<never, { data: Record<string, unknown>[] }>('GET', url, { params: paramsNew, ...config })
  }

  public updateStatus(id: number, params: Record<string, unknown> = {}, config: AxiosRequestConfig = {}) {
    const path = `${id}/update-status/`
    const [url] = this.getUrl(path)

    return this.request<never, { data: Record<string, unknown>[] }>('POST', url, { params, ...config })
  }

  public updateScannable(
    id: number,
    data: {
      data: Array<{
        values: (string | number)[];
        image?: string[];
        ocr_result_ids?: number[];
      }>;
    },
    config: AxiosRequestConfig = {},
  ) {
    const path = `${id}/scannable/update/`
    const [url] = this.getUrl(path)

    return this.request<never, { data: { inserted_ocr_result_ids: number[] } }>('POST', url, { data, ...config })
  }

  public getNotStarted(
    data_collection_id: DataCollection['id'],
    params: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = '/not-started'
    const [url] = this.getUrl(path, params)
    return this.request<never, { data: Record<string, unknown>[]; count: number }>('GET', url, { params, ...config })
  }

  public getStats(
    params: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = '/statistics'
    const [url] = this.getUrl(path, params)
    return this.request<never, { data: Record<string, unknown>[]; count: number }>('GET', url, { params, ...config })
  }

  public getMunStats(
    params: Record<string, unknown> = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = '/mun-statistics'
    const [url] = this.getUrl(path, params)
    return this.request<never, { data: Record<string, unknown>[]; count: number }>('GET', url, { params, ...config })
  }
}
